export const normalizeRole = (role) =>
  String(role || "")
    .toLowerCase()
    .trim();

const FULL_ACCESS_ROLES = ["owner", "admin"];

/** Longest prefix first so /products/add wins over /products */
const PATH_RULES = [
  { prefix: "/integrations", roles: ["owner", "admin"] },
  { prefix: "/settings/ebay", roles: ["owner", "admin"] },
  { prefix: "/settings/shopify", roles: ["owner", "admin"] },
  { prefix: "/settings/general", roles: ["owner", "admin", "seller manager"] },
  { prefix: "/invite-users", roles: ["owner", "admin", "seller manager"] },
  { prefix: "/batches/print", roles: ["owner", "admin", "seller manager"] },
  { prefix: "/batches", roles: ["owner", "admin", "seller manager"] },
  {
    prefix: "/order-dashboard",
    roles: ["owner", "admin", "seller manager", "seller"],
  },
  {
    prefix: "/orders",
    roles: ["owner", "admin", "seller", "rider", "outbound"],
  },
  {
    prefix: "/products/add",
    roles: ["owner", "admin", "editor", "seller", "seller manager"],
  },
  { prefix: "/products/update", roles: ["owner", "admin"] },
  {
    prefix: "/products/",
    roles: ["owner", "admin", "editor", "seller", "seller manager"],
  },
  {
    prefix: "/products",
    roles: ["owner", "admin", "editor", "seller", "seller manager"],
  },
  {
    prefix: "/vinted-products",
    roles: ["owner", "admin", "seller manager"], // seller manager bhi daal diya
  },
  {
    prefix: "/sorter",
    roles: ["owner", "admin", "editor", "seller", "seller manager"],
  },
  {
    prefix: "/listing-agent",
    roles: ["owner", "admin", "editor", "seller manager"],
  },
  {
    prefix: "/automation/ebay",
    roles: ["owner", "admin", "editor", "seller", "seller manager"],
  },
  { prefix: "/dashboard", roles: ["owner", "admin"] },
  { prefix: "/pos", roles: ["owner", "admin"] },
  { prefix: "/multimodel", roles: ["owner", "admin"] },
  { prefix: "/kpi-dashboard", roles: ["owner", "admin", "seller manager"] },
  { prefix: "/logger-test", roles: ["owner", "admin"] },
  { prefix: "/services", roles: ["owner", "admin"] },
  { prefix: "/chatbot", roles: ["owner", "admin", "seller manager"] },
];

export const hasRoleAccess = (userRole, allowedRoles = []) => {
  const role = normalizeRole(userRole);
  if (FULL_ACCESS_ROLES.includes(role)) return true;
  return allowedRoles.map(normalizeRole).includes(role);
};

export const canAccessPath = (userRole, pathname = "") => {
  const role = normalizeRole(userRole);
  if (FULL_ACCESS_ROLES.includes(role)) return true;

  const path = pathname.split("?")[0];
  const rule = PATH_RULES.find(
    (entry) => path === entry.prefix || path.startsWith(`${entry.prefix}/`),
  );

  if (!rule) return false;
  return rule.roles.includes(role);
};

export const getDefaultPathForRole = (userRole) => {
  const role = normalizeRole(userRole);
  if (role === "seller manager") return "/products";
  if (role === "seller" || role === "editor") return "/sorter";
  if (["rider", "outbound"].includes(role)) {
    return "/orders";
  }
  if (FULL_ACCESS_ROLES.includes(role)) return "/dashboard";
  return "/sorter";
};

export const canManageUsers = (userRole) =>
  hasRoleAccess(userRole, ["owner", "admin", "seller manager"]);

/** Roles the current user may assign when adding a new user */
export const getAssignableRolesForAdd = (currentUserRole) => {
  const current = normalizeRole(currentUserRole);
  if (current === "seller manager") return ["seller"];
  if (current === "owner") {
    return [
      "owner",
      "admin",
      "editor",
      "seller",
      "seller manager",
      "rider",
      "outbound",
    ];
  }
  if (current === "admin") {
    return ["admin", "editor", "seller", "seller manager", "rider", "outbound"];
  }
  return [];
};

export const canAddUserWithRole = (currentUserRole, roleToAdd) => {
  const role = normalizeRole(roleToAdd);
  return getAssignableRolesForAdd(currentUserRole).includes(role);
};

export const canDeleteUser = (currentUserRole, targetUserRole) => {
  const current = normalizeRole(currentUserRole);
  const target = normalizeRole(targetUserRole);

  if (!canManageUsers(current)) return false;
  if (current === "seller manager") return target === "seller";
  if (current === "admin" && target === "owner") return false;
  if (current === "editor" || current === "seller") return false;
  return true;
};

export const canEditUser = (currentUserRole, targetUserRole) => {
  const current = normalizeRole(currentUserRole);
  const target = normalizeRole(targetUserRole);

  if (!canManageUsers(current)) return false;
  if (current === "seller manager") return target === "seller";
  if (current === "admin" && target === "owner") return false;
  if (current === "editor" || current === "seller") return false;
  return true;
};

/** @deprecated Use canDeleteUser / canEditUser */
export const canModifyUser = (currentUserRole, targetUserRole) =>
  canDeleteUser(currentUserRole, targetUserRole) ||
  canEditUser(currentUserRole, targetUserRole);

/** Whether the current user may impersonate (login as) the target user */
export const canLoginAsUser = (
  currentUserRole,
  targetUserRole,
  currentUserId,
  targetUserId,
) => {
  if (currentUserId === targetUserId) return false;

  const current = normalizeRole(currentUserRole);
  const target = normalizeRole(targetUserRole);

  if (!canManageUsers(current)) return false;
  if (current === "seller manager") return target === "seller";
  if (current === "admin" && target === "owner") return false;

  return true;
};
